/**
 * Super Admin Dashboard (Enhanced V2)
 * Elevated privileges for user management, partner vetting, and system monitoring
 */

import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { superAdminService } from '../../services/api';
import KnowledgeBaseAdmin from '../KnowledgeBase/KnowledgeBaseAdmin';
import QAHistory from './QAHistory';
import RegulationList from './RegulationList';
import AnnouncementsManager from './AnnouncementsManager';
import SystemSettingsPanel from './SystemSettingsPanel';
import EntitlementConsole from './EntitlementConsole';
import Navbar from '../Dashboard/components/Navbar';
import FeatureToggleModal from './FeatureToggleModal';
import TenantPolicyModal from './TenantPolicyModal';
import ArchivedTenantsTab from './ArchivedTenantsTab';
import { RELEASE_BADGE } from '../../config/release';

// Portal URLs - Internal administrative links
const AFFILIATES_PORTAL_URL = '/admin/affiliates';
const PARTNERS_PORTAL_URL = '/admin/partners';

interface Tenant {
    username: string;
    email: string;
    phone?: string;
    status: string;
    enabled: boolean;
    created: string;
    tenantId?: string;
    propertyCount?: number;
    subscriptionTier?: string;
}

interface Partner {
    id: string;
    businessName: string;
    contactEmail: string;
    partnerType: string;
    verificationStatus: 'pending' | 'verified' | 'rejected' | 'suspended';
    createdAt: string;
    credentials?: any;
    strikesCount?: number;
}

interface AuditLog {
    id: string;
    sk: string;
    timestamp: string;
    userId: string;
    userEmail?: string;
    action: string;
    details: any;
    metadata?: any;
}

export default function SuperAdmin({ isAdminMode = false }: { isAdminMode?: boolean }) {
    const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
    
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const initialTab = (queryParams.get('tab') as any) || (isAdminMode ? 'affiliates' : 'announcements');

    const [activeTab, setActiveTab] = useState<'announcements' | 'users' | 'knowledge' | 'qaHistory' | 'regulations' | 'gov' | 'audit' | 'partners' | 'affiliates' | 'properties' | 'system' | 'archived' | 'entitlements'>(initialTab);
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [partners, setPartners] = useState<Partner[]>([]);
    const [affiliates, setAffiliates] = useState<any[]>([]);
    const [govRequests, setGovRequests] = useState<any[]>([]);
    const [globalProperties, setGlobalProperties] = useState<any[]>([]);
    const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTenantForFeatures, setSelectedTenantForFeatures] = useState<string | null>(null);
    const [selectedTenantForPolicy, setSelectedTenantForPolicy] = useState<{ tenantId: string; email: string } | null>(null);

    useEffect(() => {
        fetchData();
    }, [activeTab]);

    const handleAffiliateAction = async (affiliateId: string, action: string, data: any = {}) => {
        try {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('idToken');
            const endpoint = action === 'commission'
                ? `${API}/api/super-admin/affiliates/${affiliateId}/commission`
                : `${API}/api/super-admin/affiliates/${affiliateId}/status`;
            const body = action === 'commission'
                ? { commissionRate: data.commissionRate }
                : { status: data.status, notes: data.notes || '' };

            const res = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(body)
            });
            const result = await res.json();
            if (result.success) {
                fetchData(); // Refresh
            } else {
                alert(result.message || 'Action failed');
            }
        } catch (err) {
            alert('Failed to perform action');
        }
    };

    const API = process.env.REACT_APP_API_URL || '';

    const fetchData = async () => {
        // Tabs handled by child components - skip loading state for these
        const selfHandledTabs = ['regulations', 'announcements', 'system', 'knowledge', 'qaHistory', 'archived', 'entitlements'];
        if (selfHandledTabs.includes(activeTab)) {
            setLoading(false);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            if (activeTab === 'users') {
                const res = await superAdminService.getUsers();
                setTenants(res.data.data || res.data.users || []);
            } else if (activeTab === 'partners') {
                const res = await superAdminService.getPartners();
                setPartners(res.data.partners || res.data.data || []);
            } else if (activeTab === 'properties') {
                const res = await superAdminService.getAllProperties();
                setGlobalProperties(res.data.properties || res.data.data || []);
            } else if (activeTab === 'audit') {
                const res = await superAdminService.getAuditLogs();
                if (res.data.success) setAuditLogs(res.data.data || []);
            } else if (activeTab === 'affiliates') {
                const res = await superAdminService.getAffiliates();
                if (res.data.success) setAffiliates(res.data.data || []);
            } else if (activeTab === 'gov') {
                const res = await superAdminService.getRegulatorRequests();
                if (res.data.success) setGovRequests(res.data.data || []);
            }
        } catch (err: any) {
            console.error(`Super Admin fetch error (${activeTab}):`, err);
            setError(err.response?.data?.message || err.message || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleImpersonate = async (email: string) => {
        try {
            const res = await superAdminService.impersonate(email);
            const { tenantId, token } = res.data;

            localStorage.setItem('impersonate_token', token);
            localStorage.setItem('impersonate_tenant', tenantId);

            alert(`Impersonating ${email}. Redirecting to dashboard...`);
            window.location.href = `/dashboard?impersonate=${tenantId}`;
        } catch (err: any) {
            console.error('Impersonation error:', err);
            alert('Failed to impersonate: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleDownloadAudit = async () => {
        try {
            const response = await superAdminService.downloadAuditLogs();
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `audit_log_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err: any) {
            console.error('Audit export error:', err);
            alert('Failed to exported logs: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleVerifyPartner = async (partnerId: string, status: 'verified' | 'rejected') => {
        const notes = prompt(`Enter ${status} notes (optional):`) || '';
        try {
            await superAdminService.verifyPartner(partnerId, status, notes);
            alert(`Partner ${status} successfully`);
            fetchData();
        } catch (err: any) {
            alert('Verification failed: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleDeleteUser = async (username: string) => {
        if (!window.confirm(`Are you sure you want to delete ${username}?`)) return;
        try {
            await superAdminService.deleteUser(username);
            alert('User deleted successfully');
            fetchData();
        } catch (err: any) {
            console.error('Delete user error:', err);
            alert('Failed to delete user: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleForcePasswordReset = async (username: string, email: string) => {
        if (!window.confirm(
            `Force password reset for ${email}?\n\n` +
            `This will:\n` +
            `  • Invalidate every active session immediately\n` +
            `  • Email the user a temporary password\n` +
            `  • Require them to set a new password on next login\n\n` +
            `SAML / SSO users will be skipped automatically.\n\nProceed?`
        )) return;
        try {
            const res = await superAdminService.forcePasswordReset(username);
            const data: any = res.data;
            alert(data?.message || 'Password reset triggered.');
        } catch (err: any) {
            console.error('Force password reset error:', err);
            alert('Failed to reset password: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleApproveGov = async (requestId: string) => {
        if (!window.confirm('Approve access for this local gov officer?')) return;
        try {
            await superAdminService.approveRegulatorRequest(requestId);
            alert('Access approved successfully');
            fetchData();
        } catch (err: any) {
            alert('Approval failed: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleIssueStrike = async (partnerId: string) => {
        const reason = prompt('Reason for Yellow Card (Strike):');
        if (reason === null) return;
        try {
            await superAdminService.issueStrike(partnerId, reason);
            alert('Yellow card issued');
            fetchData();
        } catch (err: any) {
            alert('Failed to issue strike: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleIssueGreenCard = async (partnerId: string) => {
        const reason = prompt('Reason for Green Card (Commendation):');
        if (reason === null) return;
        try {
            await superAdminService.issueGreenCard(partnerId, reason);
            alert('🟢 Green card issued!');
            fetchData();
        } catch (err: any) {
            alert('Failed to issue green card: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleDeletePartner = async (partnerId: string) => {
        if (!window.confirm('CRITICAL: Permanently delete this partner from the system? This cannot be undone.')) return;
        try {
            await superAdminService.deletePartner(partnerId);
            alert('Partner deleted successfully');
            fetchData();
        } catch (err: any) {
            alert('Delete failed: ' + (err.response?.data?.message || err.message));
        }
    };
    
    const handleExterminate = async (tenantId: string, email: string) => {
        const confirm = window.confirm(
            `☢️ CRITICAL WARNING: EXTERMINATE ALL DATA FOR ${email}?\n\n` +
            `This will PERMANENTLY delete:\n` +
            `- All properties and certificates\n` +
            `- All tenants and job history\n` +
            `- All compliance records and audit logs\n` +
            `- All Cognito user accounts for this client\n\n` +
            `THIS ACTION IS IRREVERSIBLE. Are you ABSOLUTELY sure?`
        );

        if (!confirm) return;

        const secondConfirm = prompt(`To proceed, type the word "EXTERMINATE" for tenant ${tenantId}:`);
        if (secondConfirm !== 'EXTERMINATE') return alert('Aborted. Confirmation text mismatch.');

        try {
            setLoading(true);
            const res = await superAdminService.exterminateTenant(tenantId, secondConfirm);
            alert(res.data.message || 'Tenant exterminated successfully.');
            fetchData();
        } catch (err: any) {
            console.error('Exterminate failed:', err);
            alert('CRITICAL ERROR: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleIssueAffiliateStrike = async (affiliateId: string) => {
        const reason = prompt('Reason for Yellow Card (Strike):');
        if (reason === null) return;
        try {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('idToken');
            const res = await fetch(`${API}/api/super-admin/affiliates/${affiliateId}/strike`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ reason })
            });
            const result = await res.json();
            if (result.success) {
                alert(result.message);
                fetchData();
            } else {
                alert(result.message || 'Action failed');
            }
        } catch (err) {
            alert('Failed to issue strike');
        }
    };

    const handleDeleteAffiliate = async (affiliateId: string) => {
        if (!window.confirm('CRITICAL: Permanently delete this affiliate? This cannot be undone.')) return;
        try {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('idToken');
            const res = await fetch(`${API}/api/super-admin/affiliates/${affiliateId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            const result = await res.json();
            if (result.success) {
                alert('Affiliate deleted successfully');
                fetchData();
            } else {
                alert(result.message || 'Delete failed');
            }
        } catch (err) {
            alert('Failed to delete affiliate');
        }
    };


    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Navbar />

            <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 flex-1">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                                <i className={`fas ${isAdminMode ? 'fa-briefcase' : 'fa-shield-check'} text-emerald-600 mr-3`}></i>
                                {isAdminMode ? 'Admin Management Portal' : 'Super Admin Control Panel'}
                                <span className="ml-4 bg-indigo-600 text-white text-[10px] font-black px-2 py-0.5 rounded shadow-sm align-middle">{RELEASE_BADGE}</span>
                            </h1>
                            <p className="mt-2 text-sm text-gray-600">
                                {isAdminMode ? 'Vetting, affiliate management, and operational oversight' : 'System-wide management and partner orchestration'}
                            </p>
                        </div>
                        <div className="flex flex-col items-end space-y-2">
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2 mb-2 w-full text-center">
                                <span className="text-emerald-800 font-semibold text-sm">
                                    <i className="fas fa-shield-check mr-2"></i>
                                    ADMIN PRIVILEGES ACTIVE
                                </span>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setActiveTab('affiliates')}
                                    className={`inline-flex items-center px-3 py-2 rounded-lg text-xs font-bold transition-all border ${activeTab === 'affiliates' ? 'bg-blue-600 text-white border-blue-700' : 'bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200'}`}
                                    title="Access Affiliates Management Tab"
                                >
                                    <i className="fas fa-link mr-2"></i>
                                    Affiliates
                                </button>
                                <button
                                    onClick={() => setActiveTab('partners')}
                                    className={`inline-flex items-center px-3 py-2 rounded-lg text-xs font-bold transition-all border ${activeTab === 'partners' ? 'bg-purple-600 text-white border-purple-700' : 'bg-purple-100 text-purple-800 border-purple-300 hover:bg-purple-200'}`}
                                    title="Access Partners Management Tab"
                                >
                                    <i className="fas fa-handshake mr-2"></i>
                                    Partners
                                </button>
                                <button
                                    onClick={handleDownloadAudit}
                                    className="inline-flex items-center px-3 py-2 bg-gray-900 text-white rounded-lg text-xs font-bold hover:bg-emerald-600 transition-all shadow-sm"
                                >
                                    <i className="fas fa-file-export mr-2"></i>
                                    AUDIT (CSV)
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Tabs (Menu Re-ordering V3) */}
                <div className="flex flex-wrap gap-1 bg-white p-2 rounded-xl shadow-sm border border-gray-200 mb-8">
                    <button
                        onClick={() => setActiveTab('announcements')}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${activeTab === 'announcements' ? 'bg-zinc-900 text-white shadow' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                    >
                        <i className="fas fa-bullhorn mr-2"></i>Alerts
                    </button>
                    <button
                        onClick={() => setActiveTab('users')}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${activeTab === 'users' ? 'bg-zinc-900 text-white shadow' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                    >
                        <i className="fas fa-users mr-2"></i>Users
                    </button>
                    <button
                        onClick={() => setActiveTab('knowledge')}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${activeTab === 'knowledge' ? 'bg-zinc-900 text-white shadow' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                    >
                        <i className="fas fa-book mr-2"></i>Compliance Knowledge Base
                    </button>
                    <button
                        onClick={() => setActiveTab('qaHistory')}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${activeTab === 'qaHistory' ? 'bg-zinc-900 text-white shadow' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                    >
                        <i className="fas fa-history mr-2"></i>Q&A History
                    </button>
                    <button
                        onClick={() => setActiveTab('regulations')}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${activeTab === 'regulations' ? 'bg-zinc-900 text-white shadow' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                    >
                        <i className="fas fa-gavel mr-2"></i>Regs
                    </button>
                    <button
                        onClick={() => setActiveTab('gov')}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${activeTab === 'gov' ? 'bg-zinc-900 text-white shadow' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                    >
                        <i className="fas fa-university mr-2"></i>Gov Access
                    </button>
                    <button
                        onClick={() => setActiveTab('audit')}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${activeTab === 'audit' ? 'bg-zinc-900 text-white shadow' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                    >
                        <i className="fas fa-history mr-2"></i>Audit
                    </button>
                    <button
                        onClick={() => setActiveTab('partners')}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${activeTab === 'partners' ? 'bg-zinc-900 text-white shadow' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                    >
                        <i className="fas fa-handshake mr-2"></i>Partners
                    </button>
                    <button
                        onClick={() => setActiveTab('affiliates')}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${activeTab === 'affiliates' ? 'bg-zinc-900 text-white shadow' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                    >
                        <i className="fas fa-link mr-2"></i>Affiliates
                    </button>
                    <button
                        onClick={() => setActiveTab('archived')}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${activeTab === 'archived' ? 'bg-zinc-900 text-white shadow' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                    >
                        <i className="fas fa-box-archive mr-2"></i>Archived Tenants
                    </button>
                    <button
                        onClick={() => setActiveTab('system')}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${activeTab === 'system' ? 'bg-zinc-900 text-white shadow' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                    >
                        <i className="fas fa-sliders-h mr-2"></i>System Settings
                    </button>
                    {/* F-305: Entitlement Console (value-protection controls) */}
                    <button
                        onClick={() => setActiveTab('entitlements')}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${activeTab === 'entitlements' ? 'bg-zinc-900 text-white shadow' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                    >
                        <i className="fas fa-shield-halved mr-2"></i>Entitlements
                    </button>
                </div>

                {/* Search Bar (shared across tabs except regulations, announcements, system, archived) */}
                {activeTab !== 'regulations' && activeTab !== 'audit' && activeTab !== 'announcements' && activeTab !== 'system' && activeTab !== 'archived' && activeTab !== 'entitlements' && (
                    <div className="mb-6">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder={`Search ${activeTab}...`}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zinc-800"
                            />
                            <i className="fas fa-search absolute left-4 top-4 text-gray-400"></i>
                        </div>
                    </div>
                )}

                {/* Content */}
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent mb-4"></div>
                        <p className="text-gray-500">Retrieving system data...</p>
                    </div>
                ) : error ? (
                    <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-8 text-center">
                        <i className="fas fa-lock text-zinc-400 text-5xl mb-4"></i>
                        <h3 className="text-lg font-bold text-zinc-900 mb-2">Access Denied or System Error</h3>
                        <p className="text-zinc-700 max-w-md mx-auto mb-6">{error}</p>
                        <button onClick={fetchData} className="px-6 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition">
                            Retry Authentication
                        </button>
                    </div>
                ) : (
                    <div className="bg-white shadow-xl rounded-2xl border border-gray-100 overflow-hidden">
                        {activeTab === 'users' && (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User Account</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {tenants.filter(t => t.email?.includes(searchTerm) || t.username?.includes(searchTerm)).map((user) => (
                                        <tr key={user.username} className="hover:bg-emerald-50/30 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="h-10 w-10 bg-emerald-50 rounded-lg flex items-center justify-center text-emerald-600 font-bold">
                                                        {user.email?.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-bold text-gray-900">{user.email}</div>
                                                        <div className="text-xs text-gray-500">{user.username}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex flex-col gap-1">
                                                    <span className={`px-2 py-1 text-xs font-bold rounded-md inline-block w-fit ${user.status === 'CONFIRMED' ? 'bg-emerald-100 text-emerald-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                                        {user.status}
                                                    </span>
                                                    <span className={`px-2 py-1 text-xs font-bold rounded-md inline-block w-fit ${
                                                        user.subscriptionTier
                                                            ? user.subscriptionTier.toLowerCase().includes('pro2') || user.subscriptionTier.toLowerCase().includes('enterprise')
                                                                ? 'bg-purple-100 text-purple-700'
                                                                : user.subscriptionTier.toLowerCase().includes('pro1')
                                                                    ? 'bg-blue-100 text-blue-700'
                                                                    : 'bg-indigo-100 text-indigo-700'
                                                            : 'bg-gray-100 text-gray-500'
                                                    }`}>
                                                        {user.subscriptionTier ? user.subscriptionTier.replace(/_/g, ' ') : 'Freemium'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {new Date(user.created).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                                                <button
                                                    onClick={async () => {
                                                        const daysStr = prompt(`How many days to extend trial for ${user.email}?`, "30");
                                                        if (!daysStr) return;
                                                        const days = parseInt(daysStr);
                                                        if (isNaN(days)) return alert('Please enter a valid number of days.');

                                                        try {
                                                            const res = await superAdminService.extendTrial(user.tenantId || `TENANT_${user.username}`, user.email, days);
                                                            alert(res.data.message || 'Trial extended successfully.');
                                                            fetchData(); // Refresh list
                                                        } catch (err: any) {
                                                            alert('Failed to extend trial: ' + (err.response?.data?.message || err.message));
                                                        }
                                                    }}
                                                    className="inline-flex items-center px-3 py-1 bg-gray-100 rounded text-emerald-600 hover:bg-emerald-50 text-sm font-bold transition"
                                                    title="Extend Trial"
                                                >
                                                    <i className="fas fa-gift"></i>
                                                </button>
                                                <button
                                                    onClick={() => handleImpersonate(user.email)}
                                                    className="inline-flex items-center px-3 py-1 bg-gray-100 rounded text-zinc-600 hover:bg-zinc-200 text-sm font-bold transition"
                                                    title="Impersonate User"
                                                >
                                                    <i className="fas fa-ghost"></i>
                                                </button>
                                                <button
                                                    onClick={async () => {
                                                        const limit = prompt('Enter new user limit exception (e.g. 5):');
                                                        if (!limit || isNaN(Number(limit))) return;
                                                        try {
                                                            await fetch(`${process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL}/api/super-admin/tenants/${user.tenantId || `TENANT_${user.username}`}/user-limit-exception`, {
                                                                method: 'PATCH',
                                                                headers: {
                                                                    'Content-Type': 'application/json',
                                                                    Authorization: `Bearer ${localStorage.getItem('accessToken') || localStorage.getItem('idToken')}`
                                                                },
                                                                body: JSON.stringify({ maxUsers: Number(limit), reason: 'Super Admin Override' })
                                                            });
                                                            alert('User limit exception updated successfully.');
                                                        } catch (err) {
                                                            alert('Failed to update limit.');
                                                        }
                                                    }}
                                                    className="inline-flex items-center px-3 py-1 bg-gray-100 rounded text-zinc-600 hover:bg-zinc-200 text-sm font-bold transition"
                                                    title="Update User Limit Exception"
                                                >
                                                    <i className="fas fa-users-cog"></i>
                                                </button>
                                                {!isAdminMode && (
                                                    <button
                                                        onClick={() => setSelectedTenantForFeatures(user.tenantId || `TENANT_${user.username}`)}
                                                        className="inline-flex items-center px-3 py-1 bg-gray-100 rounded text-indigo-600 hover:bg-indigo-50 text-sm font-bold transition"
                                                        title="Manage Enabled Features"
                                                    >
                                                        <i className="fas fa-toggle-on"></i>
                                                    </button>
                                                )}
                                                <a
                                                    href={`/admin/users?tenant_id=${user.tenantId || `TENANT_${user.username}`}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center px-3 py-1 bg-gray-100 rounded text-emerald-600 hover:bg-emerald-50 text-sm font-bold transition"
                                                    title="Manage Account Users"
                                                >
                                                    <i className="fas fa-user-edit"></i>
                                                </a>
                                                <button
                                                    onClick={() => handleForcePasswordReset(user.username, user.email)}
                                                    className="inline-flex items-center px-3 py-1 bg-gray-100 rounded text-amber-700 hover:bg-amber-50 text-sm font-bold transition"
                                                    title="Force Password Reset (invalidates sessions, emails reset link). SAML users are skipped automatically."
                                                >
                                                    <i className="fas fa-key"></i>
                                                </button>
                                                {user.tenantId && (
                                                    <button
                                                        onClick={() => setSelectedTenantForPolicy({ tenantId: user.tenantId!, email: user.email })}
                                                        className="inline-flex items-center px-3 py-1 bg-gray-100 rounded text-indigo-700 hover:bg-indigo-50 text-sm font-bold transition"
                                                        title="Password Policy Override (set custom max-age for this tenant or disable enforcement)"
                                                    >
                                                        <i className="fas fa-user-shield"></i>
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleDeleteUser(user.username)}
                                                    className="inline-flex items-center px-3 py-1 bg-gray-100 rounded text-red-600 hover:bg-red-50 text-sm font-bold transition"
                                                    title="Delete User Account (Irreversible)"
                                                >
                                                    <i className="fas fa-trash-alt"></i>
                                                </button>
                                                {/* Kill-switch only makes sense for real customer tenants.
                                                    Staff / system accounts (e.g. former employees) have no
                                                    custom:tenantId in Cognito, so there's no tenant data to
                                                    wipe — showing the button there previously produced a
                                                    silent "0 items deleted" with no explanation. Use the
                                                    trash-icon Delete User button above for offboarding
                                                    accounts like that instead. */}
                                                {isSuperAdmin && !isAdminMode && user.tenantId && (
                                                    <button
                                                        onClick={() => handleExterminate(user.tenantId!, user.email ?? '')}
                                                        className="inline-flex items-center px-3 py-1 bg-red-100 rounded text-red-700 hover:bg-red-200 text-sm font-black transition ml-2"
                                                        title="KILL SWITCH: EXTERMINATE ALL DATA"
                                                    >
                                                        <i className="fas fa-radiation"></i>
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {activeTab === 'properties' && (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Property Address</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Owner Tenant ID</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {globalProperties.filter(p => p.address?.line1?.toLowerCase().includes(searchTerm.toLowerCase())).map((prop) => (
                                        <tr key={prop.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                                                {prop.address?.line1 || prop.id}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                                {prop.tenantId}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="bg-gray-100 px-2 py-1 rounded text-xs">{prop.type || 'Residential'}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {activeTab === 'partners' && (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Partner Business</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Verification</th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {partners.filter(p => p.businessName?.toLowerCase().includes(searchTerm.toLowerCase())).map((partner) => (
                                        <tr key={partner.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="font-bold text-gray-900">{partner.businessName}</div>
                                                <div className="text-sm text-gray-500">{partner.contactEmail}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">{partner.partnerType}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-bold w-fit ${partner.verificationStatus === 'verified' ? 'bg-emerald-100 text-emerald-800' : partner.verificationStatus === 'suspended' ? 'bg-zinc-900 text-white' : 'bg-yellow-100 text-yellow-800'}`}>
                                                        {partner.verificationStatus.toUpperCase()}
                                                    </span>
                                                    {partner.strikesCount && partner.strikesCount > 0 ? (
                                                        <span className="text-[10px] font-black text-zinc-900 mt-1 flex items-center">
                                                            <i className="fas fa-exclamation-triangle mr-1"></i>
                                                            {partner.strikesCount} YELLOW CARD{partner.strikesCount > 1 ? 'S' : ''}
                                                        </span>
                                                    ) : null}
                                                    {(partner as any).greenCards && (partner as any).greenCards > 0 ? (
                                                        <span className="text-[10px] font-black text-emerald-700 mt-1 flex items-center">
                                                            <i className="fas fa-award mr-1"></i>
                                                            {(partner as any).greenCards} GREEN CARD{(partner as any).greenCards > 1 ? 'S' : ''}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right">
                                                <div className="flex justify-end space-x-3">
                                                    {partner.verificationStatus?.toLowerCase() === 'pending' && (
                                                        <>
                                                            <button onClick={() => handleVerifyPartner(partner.id, 'verified')} className="text-emerald-600 hover:text-emerald-800" title="Verify">
                                                                <i className="fas fa-check"></i>
                                                            </button>
                                                            <button onClick={() => handleVerifyPartner(partner.id, 'rejected')} className="text-zinc-400 hover:text-zinc-600" title="Reject">
                                                                <i className="fas fa-times"></i>
                                                            </button>
                                                        </>
                                                    )}
                                                    {partner.verificationStatus?.toLowerCase() === 'verified' && (
                                                        <>
                                                            <button onClick={() => handleIssueGreenCard(partner.id)} className="text-emerald-500 hover:text-emerald-700" title="Issue Green Card (+1)">
                                                                <i className="fas fa-award"></i>
                                                            </button>
                                                            <button onClick={() => handleIssueStrike(partner.id)} className="text-amber-500 hover:text-amber-600" title="Issue Yellow Card (-1)">
                                                                <i className="fas fa-exclamation-circle"></i>
                                                            </button>
                                                        </>
                                                    )}
                                                    {!isAdminMode && (
                                                        <button onClick={() => handleDeletePartner(partner.id)} className="text-gray-400 hover:text-zinc-900" title="Delete Account">
                                                            <i className="fas fa-trash-alt"></i>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {activeTab === 'audit' && (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Timestamp</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Action</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">User</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Details</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {auditLogs.map((log, index) => (
                                        <tr key={log.sk || log.id || index} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                                                {new Date(log.timestamp).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="bg-zinc-100 text-zinc-800 px-2 py-1 rounded text-xs font-bold">{log.action}</span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {log.userId || log.userEmail || 'System'}
                                            </td>
                                            <td className="px-6 py-4 text-xs text-gray-500 max-w-xs truncate">
                                                {log.metadata ? JSON.stringify(log.metadata) : JSON.stringify(log.details || {})}
                                            </td>
                                        </tr>
                                    ))}
                                    {auditLogs.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="px-6 py-10 text-center text-gray-500">No audit logs found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}

                        {activeTab === 'regulations' && (
                            <div className="p-6">
                                <RegulationList />
                            </div>
                        )}

                        {activeTab === 'announcements' && (
                            <div className="p-6">
                                <AnnouncementsManager />
                            </div>
                        )}

                        {/* AFFILIATES TAB */}
                        {activeTab === 'affiliates' && (
                            <div>
                                <div className="p-6 border-b border-gray-200">
                                    <h2 className="text-lg font-bold text-gray-900">
                                        <i className="fas fa-link text-emerald-600 mr-2"></i>
                                        Affiliate Partner Management ({affiliates.length})
                                    </h2>
                                    <p className="text-sm text-gray-500 mt-1">Approve, suspend, and manage affiliate commission rates</p>
                                </div>
                                {affiliates.length === 0 ? (
                                    <div className="p-12 text-center text-gray-500">
                                        <i className="fas fa-handshake text-gray-300 text-5xl mb-4"></i>
                                        <p>No affiliate applications yet.</p>
                                    </div>
                                ) : (
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Partner</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Code</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Commission</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Clicks</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Trials</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Conversions</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Earnings</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Agreement</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {affiliates.filter(a => !searchTerm || (a.name?.toLowerCase().includes(searchTerm.toLowerCase()) || a.email?.toLowerCase().includes(searchTerm.toLowerCase()) || a.companyName?.toLowerCase().includes(searchTerm.toLowerCase()))).map((aff: any) => (
                                                <tr key={aff.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3">
                                                        <div className="text-sm font-medium text-gray-900">{aff.name || aff.companyName || aff.email?.split('@')[0]}</div>
                                                        <div className="text-xs text-gray-500">{aff.email}</div>
                                                        {aff.company && <div className="text-xs text-gray-400">{aff.company}</div>}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-600">{aff.type || '—'}</td>
                                                    <td className="px-4 py-3">
                                                        <code className="text-xs bg-zinc-50 text-zinc-700 px-2 py-0.5 rounded">{aff.code}</code>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex flex-col">
                                                            <span className={`px-2 py-1 text-xs font-bold rounded-md ${aff.status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                                                                aff.status === 'suspended' ? 'bg-zinc-900 text-white' :
                                                                    'bg-yellow-100 text-yellow-800'
                                                                }`}>
                                                                {aff.status?.toUpperCase()}
                                                            </span>
                                                            {aff.strikesCount > 0 && (
                                                                <span className="text-[10px] font-black text-zinc-900 mt-1 flex items-center">
                                                                    <i className="fas fa-exclamation-triangle mr-1"></i>
                                                                    {aff.strikesCount} STRIKE{aff.strikesCount > 1 ? 'S' : ''}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{aff.commissionRate}%</td>
                                                    <td className="px-4 py-3 text-sm text-gray-600">{aff.totalClicks}</td>
                                                    <td className="px-4 py-3 text-sm text-zinc-600">{aff.totalTrials}</td>
                                                    <td className="px-4 py-3 text-sm text-emerald-600 font-bold">{aff.totalConversions}</td>
                                                    <td className="px-4 py-3 text-sm font-bold text-gray-900">£{(aff.totalEarnings || 0).toFixed(2)}</td>
                                                    <td className="px-4 py-3">
                                                        {aff.agreementAcceptedAt ? (
                                                            <span className="text-xs text-emerald-600" title={aff.agreementAcceptedAt}>
                                                                ✅ Accepted
                                                            </span>
                                                        ) : (
                                                            <span className="text-xs text-zinc-400 font-medium">⚠️ Not signed</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex flex-col gap-1">
                                                            {aff.status !== 'approved' && (
                                                                <button
                                                                    onClick={() => handleAffiliateAction(aff.id, 'status', { status: 'approved' })}
                                                                    className="px-3 py-1 text-xs font-bold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
                                                                >
                                                                    Approve
                                                                </button>
                                                            )}
                                                            <div className="grid grid-cols-2 gap-1">
                                                                <button
                                                                    onClick={() => {
                                                                        const rate = prompt(`Current: ${aff.commissionRate}%. Enter new rate (0-50):`);
                                                                        if (rate && !isNaN(Number(rate))) {
                                                                            handleAffiliateAction(aff.id, 'commission', { commissionRate: Number(rate) });
                                                                        }
                                                                    }}
                                                                    className="px-2 py-1 text-[10px] font-black uppercase tracking-widest bg-white text-gray-500 rounded-lg border border-gray-100 hover:border-emerald-500/30 hover:text-emerald-600 transition-all"
                                                                    title="Adjust Rate"
                                                                >
                                                                    Rate
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        const targetStatus = aff.status === 'suspended' ? 'approved' : 'suspended';
                                                                        const notes = targetStatus === 'suspended' ? (prompt('Reason for suspension (optional):') || '') : '';
                                                                        handleAffiliateAction(aff.id, 'status', { status: targetStatus, notes });
                                                                    }}
                                                                    className={`px-2 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all border ${aff.status === 'suspended' ? 'bg-zinc-900 text-white border-zinc-900 hover:bg-black' : 'bg-white text-gray-400 border-gray-100 hover:border-zinc-900 hover:text-zinc-900'}`}
                                                                    title={aff.status === 'suspended' ? 'Reactivate' : 'Suspend'}
                                                                >
                                                                    {aff.status === 'suspended' ? 'Unlock' : 'Pause'}
                                                                </button>
                                                            </div>
                                                            <button
                                                                onClick={() => handleIssueAffiliateStrike(aff.id)}
                                                                className="px-3 py-1.5 text-[10px] font-black uppercase tracking-widest bg-yellow-50 text-yellow-800 rounded-lg hover:bg-yellow-100 border border-yellow-200"
                                                            >
                                                                <i className="fas fa-exclamation-triangle mr-1"></i> Issue Yellow Card
                                                            </button>
                                                            {!isAdminMode && (
                                                                <button
                                                                    onClick={() => handleDeleteAffiliate(aff.id)}
                                                                    className="px-3 py-1.5 text-[10px] font-black uppercase tracking-widest bg-zinc-100 text-zinc-600 rounded-lg hover:bg-zinc-900 hover:text-white transition-all group"
                                                                >
                                                                    <i className="fas fa-trash-alt mr-1 group-hover:text-white"></i> Delete Affiliate
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        )}

                        {/* SYSTEM SETTINGS TAB (Super Admin Only) */}
                        {activeTab === 'system' && !isAdminMode && (
                            <div className="p-6">
                                <SystemSettingsPanel />
                            </div>
                        )}

                        {/* ENTITLEMENTS TAB (F-305 — value-protection console) */}
                        {activeTab === 'entitlements' && !isAdminMode && (
                            <div className="p-6">
                                <EntitlementConsole />
                            </div>
                        )}

                        {/* KNOWLEDGE BASE TAB */}
                        {activeTab === 'knowledge' && (
                            <div className="bg-white rounded-lg border border-gray-200">
                                <KnowledgeBaseAdmin />
                            </div>
                        )}

                        {/* Q&A HISTORY TAB (F-038) */}
                        {activeTab === 'qaHistory' && (
                            <div className="bg-white rounded-lg border border-gray-200">
                                <QAHistory />
                            </div>
                        )}

                        {/* ARCHIVED TENANTS TAB (orphan-account recovery) */}
                        {activeTab === 'archived' && (
                            <div className="bg-white rounded-lg border border-gray-200 p-6">
                                <ArchivedTenantsTab />
                            </div>
                        )}

                        {/* GOV ACCESS TAB */}
                        {activeTab === 'gov' && (
                            <div>
                                <div className="p-6 border-b border-gray-200">
                                    <h3 className="text-lg font-bold text-gray-900">Housing Authority Access Requests</h3>
                                    <p className="text-sm text-gray-500">Manage jurisdictional access for local government officers</p>
                                </div>
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Officer</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Council / Borough</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Requested</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {govRequests.map((req) => (
                                            <tr key={req.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4">
                                                    <div className="font-bold text-gray-900">{req.name || 'Anonymous Officer'}</div>
                                                    <div className="text-sm text-gray-500">{req.email}</div>
                                                </td>
                                                <td className="px-6 py-4 text-sm font-medium text-gray-700 capitalize">
                                                    {req.orgName || req.borough}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`px-2 py-1 rounded text-xs font-bold ${req.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                        {req.status?.toUpperCase()}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500">
                                                    {new Date(req.createdAt).toLocaleDateString()}
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    {req.status === 'pending' && (
                                                        <button
                                                            onClick={() => handleApproveGov(req.id)}
                                                            className="bg-emerald-600 text-white px-3 py-1 rounded text-sm font-bold hover:bg-emerald-700 transition"
                                                        >
                                                            Approve Access
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {govRequests.length === 0 && (
                                            <tr>
                                                <td colSpan={5} className="px-6 py-10 text-center text-gray-500 italic">No pending gov access requests.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {selectedTenantForFeatures && (
                <FeatureToggleModal
                    tenantId={selectedTenantForFeatures}
                    onClose={() => setSelectedTenantForFeatures(null)}
                />
            )}

            {selectedTenantForPolicy && (
                <TenantPolicyModal
                    tenantId={selectedTenantForPolicy.tenantId}
                    email={selectedTenantForPolicy.email}
                    onClose={() => setSelectedTenantForPolicy(null)}
                />
            )}
        </div>
    );
}
